import React, { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Box, Typography } from "@mui/material";
import ImageIcon from "@mui/icons-material/Image";

import AdBannersTable from "./AdBannersTable";
import { axiosConfig } from "../../../axios/axiosConfig";
import MuiLoadingSpinner from "../../common/MuiLoadingSpinner";
import UploadBannerForm from "../../common/UploadBannerForm";

const UploadBanners = () => {
  const [activeBannerItem, setActiveBannerItem] = useState({}); // active banner item that is being edited
  const todayDate = new Date().toISOString().slice(0, 10);
  const [isEditing, setIsEditing] = useState(false);

  const initialFormInput = {
    imgUrl: activeBannerItem?.screen_url || "",
    targetUrl: "",
    selectedScreen: "Dashboard",
    startDate: formatDate(activeBannerItem?.from_date) || todayDate,
    endDate: formatDate(activeBannerItem?.till_date) || "",
    plan_code: [],
    height: "0",
    deeplink: "",
  };

  const [formInput, setFormInput] = useState(initialFormInput);

  const [imgFile, setImgFile] = useState();
  const [imgPreview, setImgPreview] = useState(null);
  const [allBannersList, setAllBannersList] = useState([]);

  const handleFormInputOnChange = (event) => {
    const { name, value } = event.target;

    setFormInput((prevState) => {
      return {
        ...prevState,
        plan_code:
          name === "plan_code"
            ? typeof value === "string"
              ? value.split(",")
              : value
            : prevState.plan_code,
        [name]: value,
      };
    });

    if (name === "imgUrl") {
      setImgPreview(value);
    }
  };

  function handleImageFileChange(e) {
    setImgPreview(URL.createObjectURL(e.target.files[0]));
    setImgFile(e.target.files[0]);

    setFormInput((prev) => {
      return {
        ...prev,
        imgUrl: URL.createObjectURL(e.target.files[0]),
      };
    });
  }

  // post banner data mutation
  const postBannerDataMutation = useMutation({
    mutationFn: () => postBanner(formInput, imgFile),
    onSuccess: async (data) => {
      setAllBannersList((prevState) => {
        return [...prevState, data.response.data];
      });
      await allBannersQuery.refetch();
    },

    onError: (error) => {
      toast.error(error?.response?.data?.response || "Something went wrong");
    },
    onSettled: () => {
      setFormInput({
        imgUrl: "",
        targetUrl: "",
        selectedScreen: "Dashboard",
        startDate: todayDate,
        endDate: "",
        plan_code: [],
        height: "0",
        deeplink: "",
      });
      setImgPreview(null);
      setImgFile(null);
    },
  });

  // HANDLE SAVE / UPDATE / PATCH
  const handleUpdate = () => {};

  // HANDLE SUBMIT
  const handleSubmit = (e) => {
    e.preventDefault();

    postBannerDataMutation.mutate();
  };

  // FETCH ALL BANNERS
  const allBannersQuery = useQuery({
    retry: false,
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchInterval: false, // Disable automatic refetching
    queryKey: ["all-banners"], // dependency
    queryFn: () => fetchAllBanners(),
    onSuccess: (data) => {
      setAllBannersList(data.response.data);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.response || "Something went wrong");
    },
  });

  // EDIT BANNER ITEM FROM TABLE, when admin clicks on the edit button on AdBannersTable
  const handleEditClick = (bannerItem) => {
    setActiveBannerItem(bannerItem);
    setIsEditing(true);

    // setFormInput({
    //   imgUrl: activeBannerItem?.screen_url,
    //   targetUrl: "",
    //   selectedScreen: activeBannerItem?.screen_name,
    //   startDate: formatDate(activeBannerItem?.from_date),
    //   endDate: formatDate(activeBannerItem?.till_date),
    // })
  };

  // useEffect(() => {
  //   setFormInput({
  //     imgUrl: activeBannerItem?.screen_url,
  //     targetUrl: "",
  //     selectedScreen: activeBannerItem?.screen_name,
  //     startDate: formatDate(activeBannerItem?.from_date),
  //     endDate: formatDate(activeBannerItem?.till_date),
  //   })
  // }, [activeBannerItem])

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>
      {/* Page Header */}
      <Box
        sx={{
          background: "#04343a",
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(4,52,58,0.3)",
          p: "20px 28px",
          mb: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <ImageIcon sx={{ color: "#fff", fontSize: 32 }} />
        <Box>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "20px" }}>
            Upload Banners
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "13px" }}>
            Upload and manage advertisement banners
          </Typography>
        </Box>
      </Box>

      {/* Upload Banner Form */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
          overflow: "hidden",
          mb: 3,
        }}
      >
        <Box sx={{ background: "#04343a", px: "20px", py: "12px" }}>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "15px" }}>
            Banner Upload Form
          </Typography>
        </Box>
        <Box sx={{ p: 3 }}>
          <UploadBannerForm
            imgPreview={imgPreview}
            formInput={formInput}
            handleFormInputOnChange={handleFormInputOnChange}
            handleImageFileChange={handleImageFileChange}
            handleSubmit={handleSubmit}
            handleUpdate={handleUpdate}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
          />
        </Box>
      </Box>

      {/* Banner Details Table */}
      {allBannersQuery?.isLoading ? (
        <MuiLoadingSpinner />
      ) : (
        <AdBannersTable
          allBannersList={allBannersList}
          setAllBannersList={setAllBannersList}
          handleEditClick={handleEditClick}
        />
      )}
    </Box>
  );
};

export default UploadBanners;

// UTILITIES
const fetchAllBanners = async () => {
  // if (!watchlistId) return;

  const res = await axiosConfig({
    method: "get",
    url: "/advertisement/screens/banner/admin",
    data: {},
  });

  return res.data;
};

// post banner to backend
const postBanner = async (formInput, img_file) => {
  // if (!watchlistId) return;

  // return;

  const formData = new FormData();
  formData.append("banner", img_file);
  formData.append("screen", formInput.selectedScreen);
  formData.append("from_date", formInput.startDate);
  formData.append("till_date", formInput.endDate);
  formData.append("plan_code", formInput.plan_code.join(", "));
  formData.append("height", formInput.height ?? 0);
  if (formInput.deeplink?.trim()) {
    formData.append("deeplink", formInput.deeplink.trim());
  }

  const res = await axiosConfig({
    method: "post",
    url: "advertisement/screens/banner",
    data: formData,
  });

  return res.data;
};

// convert date
const formatDate = (dateString) => {
  if (!dateString || dateString.trim() === "") return;

  return new Date(dateString).toISOString().slice(0, 10);
};
